import { api } from "./client";
import type { ApiResponse } from "./types";

// ── Tickets that admit several people ──────────────────────────────

/** A ticket kind as the organiser manages it (GET /events/{id}/tiers/manage). */
export interface AdmitsTier {
  id: string;
  name: string;
  priceCents: number;
  /** How many people one ticket lets in: 4 for "a family of four". */
  admitsCount: number;
  /** -1 is unlimited. Counted in tickets, never in people. */
  quantityTotal: number;
  quantitySold: number;
  isActive: boolean;
}

export function listAdmitsTiers(eventId: string) {
  return api.get<ApiResponse<{ tiers: AdmitsTier[] }>>(`/events/${eventId}/tiers/manage`);
}

/** POST /events/{eventId}/tiers with how many people a ticket lets in. */
export function createAdmitsTier(
  eventId: string,
  body: { name: string; priceCents: number; quantityTotal: number; admitsCount: number },
) {
  return api.post<ApiResponse<AdmitsTier>>(`/events/${eventId}/tiers`, body);
}

/** PATCH /events/{eventId}/tiers/{tierId}: 409 TIER_HAS_SOLD_TICKETS once anybody holds the kind. */
export function setTierAdmits(eventId: string, tierId: string, admitsCount: number) {
  return api.patch<ApiResponse<AdmitsTier>>(`/events/${eventId}/tiers/${tierId}`, { admitsCount });
}

export interface AdmissionsStats {
  totalIssued: number;
  totalCheckedIn: number;
  /** People, not tickets. */
  admissions: { issued: number; checkedIn: number };
  perTier: Array<{ tierId: string; tierName: string; admitsCount: number; issued: number; checkedIn: number }>;
}

/** GET /events/{eventId}/check-in-stats, read for the people the tickets let in. */
export function admissionsStats(eventId: string) {
  return api.get<ApiResponse<AdmissionsStats>>(`/events/${eventId}/check-in-stats`);
}

// ── Volunteering for every host ────────────────────────────────────

export interface VolunteerShift {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  spotsAvailable: number;
  spotsFilled: number;
}

export interface EventVolunteerJob {
  id: string;
  title: string;
  description: string;
  category: string;
  minimumAge: number | null;
  status: string;
  spotsAvailable: number;
  spotsFilled: number;
  shifts: VolunteerShift[];
}

export interface NewVolunteerJob {
  title: string;
  description: string;
  category: string;
  minimumAge?: number;
  shifts: Array<{ date: string; startTime: string; endTime: string; spotsAvailable: number }>;
}

/** POST /events/{eventId}/volunteer-opportunities: the event's organisers, for any host. */
export function createEventVolunteerJob(eventId: string, job: NewVolunteerJob) {
  return api.post<ApiResponse<EventVolunteerJob>>(`/events/${eventId}/volunteer-opportunities`, job);
}

/** GET /events/{eventId}/volunteer-opportunities: public, counts only. */
export function listEventVolunteerJobs(eventId: string) {
  return api.get<ApiResponse<{ items: EventVolunteerJob[] }>>(`/events/${eventId}/volunteer-opportunities`);
}

/** Sign up for one shift, with any children as age bands ("13-15"). */
export function signUpForShift(jobId: string, shiftId: string, childAgeBands: string[] = []) {
  return api.post<ApiResponse<{ id: string; status: string }>>(`/volunteer-opportunities/${jobId}/signup`, {
    shiftId,
    childAgeBands,
  });
}

export function withdrawFromShift(jobId: string, shiftId: string) {
  return api.delete<void>(`/volunteer-opportunities/${jobId}/signup?shiftId=${shiftId}`);
}

export interface JobSignup {
  id: string;
  shiftId: string | null;
  status: string;
  childAgeBands: string[];
  user: { id: string; name: string };
}

/** The host's list: names stay here. */
export function listJobSignups(jobId: string) {
  return api.get<ApiResponse<{ signups: JobSignup[] }>>(`/volunteer-opportunities/${jobId}/signups`);
}

export function acceptJobSignup(jobId: string, signupId: string) {
  return api.post<ApiResponse<unknown>>(`/volunteer-opportunities/${jobId}/signups/${signupId}/accept`, {});
}

// ── The album, for each reader ─────────────────────────────────────

export interface AlbumSummary {
  photos: number;
  videos: number;
  contributors: number;
  /** Files by the reader's matches who went; null signed out. */
  fromMatches: number | null;
  /** Those matches, most files first, at most 12. */
  matchesWhoAdded: Array<{ id: string; name: string; avatarUrl: string | null }>;
}

/** The event detail's `media` block (GET /events/{id}). */
export async function albumSummary(eventId: string) {
  const res = await api.get<ApiResponse<{ media: AlbumSummary }>>(`/events/${eventId}`);
  return res.data.media;
}

/** "Check every photo and video first" (PATCH /events/{id} `mediaReview`), organisers only. */
export function setMediaReview(eventId: string, mediaReview: "auto" | "host") {
  return api.patch<ApiResponse<{ mediaReview: string | null }>>(`/events/${eventId}`, { mediaReview });
}

/** The event detail's `mediaReview`: the setting for organisers, null for anyone else. */
export async function mediaReviewOf(eventId: string) {
  const res = await api.get<ApiResponse<{ mediaReview: string | null }>>(`/events/${eventId}`);
  return res.data.mediaReview;
}

/** Follow an organiser page and choose its volunteer notices (off unless chosen). */
export function followOrganizerForVolunteerNotices(eventOrganizerId: string) {
  return api.post<ApiResponse<unknown>>(`/event-organizers/${eventOrganizerId}/follow`, { notifyVolunteerOpps: true });
}
